import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { InstantSearch, InstantSearchSSRProvider } from 'react-instantsearch';

import { Configure } from '@/components/Configure';
import { InstantSearchStateUpdater } from '@/components/InstantSearchUpdater/InstantSearchUpdater';
import { Spacer } from '@/components/Spacer';
import { ListingPageDocument } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import {
  buildQueryParamsFromSearchUiState,
  buildUiStateFromURL,
  getIndexName,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { SearchUiState } from '@/integrations/algolia/types';
import { PAGE_CONTEXT } from '@/integrations/algolia/types';
import { Default } from '@/layouts/Default';
import { FILTERS_FETCH_LIMIT } from '@/lib/const';
import { getRegionFromLocale } from '@/lib/regions';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { listingActions } from '@/states/listingState';

import { usePreRenderCmsSections } from './helpers';
import type { AllTheWatchesViewProps } from './types';

export const AllTheWatchesView = ({
  algoliaFilters,
  algoliaUserToken,
  allTheWatchesCmsData,
  draftMode,
  instantSearchProps,
  serverState,
}: AllTheWatchesViewProps) => {
  const liveAllTheWatchesCmsData = useContentfulLiveUpdates(
    allTheWatchesCmsData,
    { query: ListingPageDocument },
  );
  const { cmsSectionAll } = usePreRenderCmsSections(liveAllTheWatchesCmsData);
  const { asPath, locale, push, query } = useRouter();
  const pageIndex = query?.page && parseInt(query.page as string);
  const indexName = getIndexName('PRODUCT', locale);
  const lastUiState = useRef<SearchUiState | null>(null);

  const uiStateFromURL = useMemo(() => {
    const region = getRegionFromLocale(locale);
    return buildUiStateFromURL(
      asPath,
      indexName,
      undefined,
      undefined,
      region.locale,
      serverState,
      'EN_US', // URL values are in English, translate to current locale (ES) for UI
    );
  }, [asPath, indexName, locale, serverState]);

  const syncStateWithUrl = useCallback(
    debounce((uiState: SearchUiState) => {
      try {
        const queryParams = buildQueryParamsFromSearchUiState(
          uiState,
          indexName,
          query,
          serverState,
          'EN_US', // Always write URL values in English
        );
        const cleanPathname = asPath.split('?')[0];
        push({ pathname: cleanPathname, query: queryParams }, undefined, {
          shallow: true,
        });
      } catch (error) {
        console.error('Failed to sync state with URL', error);
      }
    }, 300),
    [indexName, push, query, serverState],
  );

  useEffect(() => {
    listingActions.updateListingType('WATCH');
    listingActions.updateModalState({ status: 'CLOSE', openedTab: null });
  }, []);

  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
    mockContext: PAGE_CONTEXT.PLP_WATCHES,
  });

  // Destructure to remove initialUiState and serverState from instantSearchProps
  // to prevent them from overriding our translated uiStateFromURL and serverState prop
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    initialUiState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    serverState: _serverStateFromProps,
    ...restInstantSearchProps
  } = (instantSearchProps as any) || {};

  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        indexName={indexName}
        initialUiState={uiStateFromURL}
        insights={false}
        onStateChange={({ setUiState, uiState }) => {
          if (!isEqual(uiState, lastUiState.current)) {
            lastUiState.current = uiState;
            setUiState(uiState);
            syncStateWithUrl(uiState);
          }
        }}
        searchClient={algoliaSearchClient}
        {...restInstantSearchProps}
      >
        <InstantSearchStateUpdater
          key={asPath}
          expectedUiState={uiStateFromURL}
        />
        <Configure
          analyticsTags={[
            'platform:web',
            'product:watches',
            'page:listing',
            'context:all-watches',
          ]}
          clickAnalytics={true}
          distinct={true}
          facetingAfterDistinct={true}
          facets={['*']}
          filters={algoliaFilters}
          maxValuesPerFacet={FILTERS_FETCH_LIMIT}
          userToken={algoliaUserToken}
          {...(typeof pageIndex === 'number' && pageIndex > 0
            ? { page: pageIndex - 1 }
            : {})}
        />
        {cmsSectionAll}
        <Spacer height={32} />
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

AllTheWatchesView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <Default
      cmsData={page.props.cmsDefaultLayoutData}
      draftMode={page.props.draftMode}
    >
      {page}
    </Default>
  </DraftModeContext.Provider>
);
